// ---------------------------------------------------------------------------
// Régua de Cobrança — derivações que o backend NÃO manda (guia §4). Decisão
// nossa, isolada aqui em helpers puros para o corte existir num lugar só.
// ---------------------------------------------------------------------------

import {
   ACTION_LABELS,
   ACTOR_TAGS,
   CHANNEL_LABELS,
   TRACK_LABELS,
   UPDATE_FIELD_TITLES,
} from '../constants';
import { daysBetween, formatDateBR } from '../../utils/dunningFormat';

// Faixas de atraso (aging) a partir de `oldest_due_date`: ≤15, 16–30, 31–60,
// >60 dias. Corte ÚNICO — kanban, fila e drawer leem daqui.
const AGING_BUCKETS = [
   { max: 15, variant: 'neutral' },
   { max: 30, variant: 'warn' },
   { max: 60, variant: 'alert' },
   { max: Infinity, variant: 'critical' },
];

export function agingInfo(oldestDueDate, today) {
   const days = daysBetween(oldestDueDate, today);
   if (days === null) return { days: null, variant: 'muted' };
   const bucket = AGING_BUCKETS.find((b) => days <= b.max);
   return { days, variant: bucket.variant };
}

// ─── Trilha (handoff 0103 — 2026-08-28) ───────────────────────────────────

// Leitura defensiva: backend sem o campo (front no ar antes do 0103) → trata
// como interno, que é exatamente o comportamento de sempre.
export function isExternalTrack(caseRow) {
   return caseRow?.track === 'EXTERNAL';
}

// Caso da trilha externa NÃO se move: não é arrastável, não é alvo de drop e
// não aceita "registrar e avançar" (422 external_case_not_movable). Caso
// fechado também não. O corte mora aqui porque board, card e formulário de
// tratativa precisam responder a mesma pergunta.
export function canMoveCase(caseRow) {
   return caseRow?.status === 'OPEN' && !isExternalTrack(caseRow);
}

// Armadilha do §9: o caso externo NASCE sem responsável, e o operador do
// escritório (sem `read_all`) só enxerga o que está atribuído a ele. Entre a
// escalada e a atribuição o caso existe e o escritório não o vê — sem sinal na
// tela, a operação lê como "o título sumiu".
export function needsAssignee(caseRow) {
   return (
      isExternalTrack(caseRow) &&
      caseRow?.status === 'OPEN' &&
      !caseRow?.assigned_user
   );
}

// Agregados do caso por STATUS (handoff 0102 — 2026-08-24). Corte ÚNICO: card
// do kanban, cabeçalho de coluna, KPIs, fila e drawer leem daqui.
//
// O bug que isto resolve: `open_amount`/`open_count`/`oldest_due_date` são
// calculados pelo backend SÓ sobre títulos em aberto. Caso recebido tem, por
// definição, zero títulos em aberto — então os três zeravam e o card exibia
// "R$ 0,00 · 0 títulos · atraso —" para um caso de R$ 100 pago. O valor não
// estava perdido; estava no bloco errado.
//
// A escolha é pelo `status` do caso, NUNCA pela raia: caso cancelado não muda
// de raia, só de status (0102 §5) — fica onde estava quando foi cancelado.
//
// Leitura defensiva (`?? 0`): backend sem os campos novos → o mesmo
// comportamento de antes, sem quebrar.
export function caseFigures(caseRow) {
   if (caseRow?.status === 'SETTLED') {
      const count = caseRow.settled_count ?? 0;
      return {
         mode: 'settled',
         amount: caseRow.settled_amount ?? 0,
         count,
         amountLabel: 'Recebido',
         countLabel: `${count} ${count === 1 ? 'título' : 'títulos'} ${
            count === 1 ? 'recebido' : 'recebidos'
         }`,
         // Data do último pagamento (≠ `settled_at`, que é quando a régua
         // fechou o caso). Some do card pra não duplicar a pill "Recebido
         // DD/MM"; aparece no drawer, no lugar do atraso.
         date: caseRow.last_payment_date ?? null,
         dateLabel: 'Pago em',
         showAging: false,
      };
   }

   if (caseRow?.status === 'CANCELLED') {
      const count = caseRow.cancelled_count ?? 0;
      return {
         mode: 'cancelled',
         amount: caseRow.cancelled_amount ?? 0,
         count,
         amountLabel: 'Cancelado',
         countLabel: `${count} ${count === 1 ? 'título' : 'títulos'} ${
            count === 1 ? 'cancelado' : 'cancelados'
         }`,
         date: null,
         dateLabel: null,
         showAging: false,
      };
   }

   // TRANSFERRED (0103 §2.5): o caso ficou sem nenhum título aberto porque
   // TUDO migrou de trilha — não é "recebido" nem "cancelado", e os `open_*`
   // zeraram como em qualquer caso encerrado. Sem este branch ele cairia no
   // bloco `open` abaixo e exibiria "R$ 0,00 · 0 títulos", que é a mesma classe
   // de bug que o 0102 consertou.
   //
   // Não existe aba para este status (decisão 2026-08-30) — ele aparece quando
   // o caso esvazia com o drawer aberto em cima dele, ou pelo link de
   // contraparte da timeline.
   //
   // ⚠️ NÃO é estado terminal (D21, adendo do 0103): o caso fica ESTACIONADO e
   // volta a `OPEN` sozinho — na raia onde parou — quando um título retorna ou
   // vence. Nada aqui pode ser tratado como fim de linha.
   if (caseRow?.status === 'TRANSFERRED') {
      return {
         mode: 'transferred',
         // Sem agregado próprio no contrato: os títulos agora pertencem ao
         // caso da outra trilha, e é lá que o valor deve ser lido.
         amount: null,
         count: 0,
         amountLabel: 'Transferido',
         countLabel: 'títulos em outra trilha',
         date: null,
         dateLabel: null,
         showAging: false,
      };
   }

   // OPEN (e qualquer status desconhecido): exatamente como sempre foi.
   const count = caseRow?.open_count ?? 0;
   return {
      mode: 'open',
      amount: caseRow?.open_amount ?? 0,
      count,
      amountLabel: 'Em aberto',
      countLabel: `${count} ${count === 1 ? 'título' : 'títulos'}`,
      date: null,
      dateLabel: null,
      showAging: true,
   };
}

// Pill de compromisso dos cards/fila (microcópia do protótipo, commitOf).
export function commitPill(caseRow, today) {
   if (caseRow.status === 'SETTLED') {
      return {
         label: `Recebido ${formatDateBR(caseRow.settled_at)}`,
         variant: 'success',
      };
   }
   if (caseRow.status === 'CANCELLED') {
      return { label: 'Cancelado', variant: 'neutral' };
   }
   if (caseRow.status === 'TRANSFERRED') {
      return { label: 'Transferido', variant: 'neutral' };
   }
   if (!caseRow.next_action_at) {
      return { label: 'Sem compromisso', variant: 'critical' };
   }
   const late = daysBetween(caseRow.next_action_at, today);
   if (late > 0) return { label: `Atrasado ${late}d`, variant: 'critical' };
   if (late === 0) return { label: 'Hoje', variant: 'warn' };
   return { label: formatDateBR(caseRow.next_action_at), variant: 'info' };
}

// Compromisso no cabeçalho do drawer (somente leitura — UI_SPEC §Drawer):
// "hoje" (âmbar), "não definido" e "DD/MM/AAAA (atrasado)" (vermelho), data
// futura (branco), "—" em caso fechado.
export function commitHeader(caseRow, today) {
   if (caseRow.status !== 'OPEN') return { text: '—', tone: 'muted' };
   if (!caseRow.next_action_at) return { text: 'não definido', tone: 'danger' };
   const late = daysBetween(caseRow.next_action_at, today);
   if (late > 0) {
      return {
         text: `${formatDateBR(caseRow.next_action_at)} (atrasado)`,
         tone: 'danger',
      };
   }
   if (late === 0) return { text: 'hoje', tone: 'warn' };
   return { text: formatDateBR(caseRow.next_action_at), tone: 'normal' };
}

// Faixas do contador de tratativas: 0 → tracejado neutro; 1–3 → cinza; ≥4 →
// azul info (guia §4.4). `interactions_count` vem do backend (delta §1) — não
// somar no front.
export function treatChip(count) {
   const total = count ?? 0;
   if (total === 0) return { label: 'sem tratativa', tier: 'zero' };
   const label = total === 1 ? '1 tratativa' : `${total} tratativas`;
   return { label, tier: total >= 4 ? 'many' : 'few' };
}

// Situação do título na tabela do drawer. `awaiting` = baixa manual enviada e
// webhook ainda não chegou (estado LOCAL da sessão — nunca otimista).
export function memberStatusPill(member, awaiting) {
   if (awaiting && member.member_status === 'OPEN') {
      return { label: 'Aguardando webhook', variant: 'warn' };
   }
   if (member.member_status === 'SETTLED') {
      return {
         label: `Pago ${formatDateBR(member.charge?.payment_date)}`,
         variant: 'success',
      };
   }
   if (member.member_status === 'CANCELLED') {
      return { label: 'Cancelado', variant: 'neutral' };
   }
   return { label: 'Vencido', variant: 'critical' };
}

// ─── Timeline (merge de interações + eventos — guia §5.6) ─────────────────
//
// ⚠️ NÃO agrupar por `event_group_id` (handoff 0104 §2.2). O contrato de API
// dizia "agrupar visualmente por event_group_id (um evento = N linhas)" e essa
// instrução estava ERRADA para os campos sintéticos de título: o
// `recordChange` do backend emite uma linha por CAMPO, então escalar N títulos
// produz N grupos distintos — e a linha de `status` da origem é um terceiro.
// Renderizar uma linha por título é o comportamento correto e é o que se faz
// aqui. Se um dia quiserem colapsar o lote, o agrupamento é por
// `(action, actor_id, occurred_at)`, nunca pelo group_id.

// before/after dos eventos vêm como STRING serializada; desserializa conforme
// o `field` (ids de raia viram nome; datas de compromisso viram BR; ids de
// título viram "título #id"). `label` = before_label/after_label que o backend
// emite pra assigned_user_id (nome resolvido, alcança usuário desativado):
// responsável exibe "id - nome" — backend sem o campo ainda → só "usuário #id".
function describeEventValue(value, field, stageNameById, label) {
   if (value === null || value === undefined || value === '') return null;
   if (field === 'stage_id') return stageNameById[value] ?? `raia ${value}`;
   if (field === 'next_action_at') return formatDateBR(value);
   if (field === 'assigned_user_id') {
      return label ? `${value} - ${label}` : `usuário #${value}`;
   }
   // Trilha (0103): o valor cru é o enum — traduz, senão a timeline exibiria
   // "INTERNAL → EXTERNAL" para o operador.
   if (field === 'track') return TRACK_LABELS[value] ?? String(value);
   // Campo sintético de título (`audit.md §8.2`): **uma linha por `charge_id`**,
   // com `before`/`after` trazendo UM id como string — nunca array. Confirmado
   // no handoff 0104 §2.2; escalar N títulos gera N eventos, não um evento com
   // lista. Não voltar a desserializar lista aqui.
   if (String(field ?? '').startsWith('charge')) return `título #${value}`;
   return label ?? String(value);
}

// Lado vazio vira texto explícito nesses campos ("Sem compromisso →
// 15/08/2026" em vez de data solta) — só quando o outro lado existe.
const EMPTY_VALUE_LABELS = {
   next_action_at: 'sem compromisso',
   assigned_user_id: 'sem responsável',
};

// Ações que mudam raia/status/trilha do caso → bolinha âmbar; `settle`
// (recebimento) → verde. O tom vale pela NATUREZA da ação, independente do ator
// — um settle automático do webhook também é verde.
//
// `escalate`/`return` entram aqui (0103): mudar de trilha é a movimentação mais
// pesada do caso. `check`/`uncheck` NÃO — conferência é registro de acerto, não
// muda o estado do caso.
const STATUS_DOT_ACTIONS = new Set([
   'move_stage',
   'send_external',
   'cancel',
   'reopen',
   'escalate',
   'return',
]);

// Elo de auditoria entre as duas pontas da transferência (D22, handoff 0104).
// O evento de migração carrega, no `metadata`, o id do caso da OUTRA ponta —
// é o que permite sair de "Título #265 enviado" para o caso que o recebeu.
//
// ⚠️ Lê SÓ esta chave. `metadata` também carrega `ip` e `user_agent` do
// operador (vale para todo evento de ação humana, desde antes desta entrega):
// não repassar o objeto adiante nem renderizar cru.
//
// Ausente = evento anterior à D22 (existe no dev; em produção não há nenhum
// evento de migração antigo) ou evento sem contraparte por natureza — título
// entrando pelo webhook de vencimento, por exemplo. Nesses casos a linha fica
// sem link, como sempre foi.
const COUNTERPART_PHRASES = {
   charges_removed: 'para o caso',
   charges_added: 'do caso',
};

function readCounterpart(event) {
   const id = event?.metadata?.counterpart_dunning_id;
   if (!id) return null;
   // `status`, `create` e `next_action_at` também carregam a contraparte, mas
   // sem direção óbvia na frase — ali o rótulo é neutro.
   const phrase = COUNTERPART_PHRASES[event.field] ?? 'caso';
   return { id, label: `${phrase} #${id}` };
}

function eventDotTone(event) {
   if (event.action === 'settle') return 'success';
   if (STATUS_DOT_ACTIONS.has(event.action)) return 'status';
   return event.actor_type === 'user' ? 'user' : 'system';
}

// Linha de evento que só DUPLICA a entrada, sem acrescentar fato (decisão
// 2026-08-31). O C4 grava uma linha por CAMPO alterado, então uma transferência
// mexe no `dunning_id` do membro E no `status` do caso — e as duas linhas saem
// com a mesma ação, o mesmo ator e o mesmo timestamp. Na tela isso lê como se
// duas coisas tivessem acontecido.
//
// O que sai: só o `status` de `escalate`/`return`. Ele é consequência mecânica
// do título ter entrado ou saído (esvaziou → TRANSFERRED; recebeu título →
// volta a OPEN), e o estado atual do caso já está no cabeçalho do drawer e no
// card.
//
// O que FICA, de propósito:
// - `next_action_at` da mesma migração (decisão do Caio): o caso perdeu um
//   compromisso agendado ao esvaziar. Isso é fato próprio, não subproduto —
//   alguém precisa saber que aquele agendamento deixou de existir.
// - `status` de `settle`, `cancel`, `reopen`, `move_stage`: ali a mudança de
//   estado É o fato.
//
// Isto é supressão de EXIBIÇÃO. Nada é filtrado no backend e nada se perde na
// auditoria — `GET /:id/events` continua devolvendo tudo.
const TRANSFER_ACTIONS = new Set(['escalate', 'return']);

function isRedundantTransferRow(event) {
   return TRANSFER_ACTIONS.has(event?.action) && event?.field === 'status';
}

export function buildTimeline({ interactions, events, stages }) {
   const stageNameById = (stages ?? []).reduce(
      (acc, stage) => ({ ...acc, [String(stage.id)]: stage.name }),
      {}
   );

   const interactionItems = (interactions ?? []).map((interaction) => ({
      key: `i-${interaction.id}`,
      ts: interaction.created_at,
      dotTone: 'user',
      title: CHANNEL_LABELS[interaction.channel] ?? 'Contato',
      tag: 'Tratativa',
      tagVariant: 'info',
      body: interaction.content,
      // Anexos (handoff 0096) — só a tratativa tem; evento nunca. `id` vai
      // junto porque as rotas de anexo são aninhadas na interação. Leitura
      // defensiva: backend sem o campo → lista vazia, timeline como antes.
      interactionId: interaction.id,
      files: interaction.files ?? [],
      meta: [
         interaction.author?.nome ?? 'Autor removido',
         interaction.charge_id ? `título #${interaction.charge_id}` : null,
         interaction.promised_payment_date
            ? `prometido para ${formatDateBR(interaction.promised_payment_date)}`
            : null,
      ].filter(Boolean),
   }));

   const eventItems = (events ?? [])
      .filter((event) => !isRedundantTransferRow(event))
      .map((event) => {
         let before = describeEventValue(
            event.before,
            event.field,
            stageNameById,
            event.before_label
         );
         let after = describeEventValue(
            event.after,
            event.field,
            stageNameById,
            event.after_label
         );
         const emptyLabel = EMPTY_VALUE_LABELS[event.field];
         if (emptyLabel && (before || after)) {
            before = before ?? emptyLabel;
            after = after ?? emptyLabel;
         }
         let change = null;
         if (before && after) change = `${before} → ${after}`;
         else if (after) change = after;
         else if (before) change = before;

         let title =
            ACTION_LABELS[event.action] ?? event.field_label ?? 'Evento';
         if (event.action === 'update' && UPDATE_FIELD_TITLES[event.field]) {
            title = UPDATE_FIELD_TITLES[event.field];
         }

         // `actor { id, nome }` só existe em evento de usuário (nome null =
         // usuário apagado do banco). Leitura defensiva: backend sem o campo
         // ainda → sem nome, como antes.
         let actorName = null;
         if (event.actor_type === 'user' && event.actor) {
            actorName = event.actor.nome ?? 'Usuário removido';
         }

         // Mesma hierarquia de 3 linhas da tratativa (decisão 2026-08-17):
         // título+badge / o que mudou (body) / autor+timestamp (meta).
         return {
            key: `e-${event.id}`,
            ts: event.occurred_at,
            dotTone: eventDotTone(event),
            title,
            tag: ACTOR_TAGS[event.actor_type] ?? 'Sistema',
            tagVariant: 'neutral',
            body: change,
            counterpart: readCounterpart(event),
            meta: [actorName].filter(Boolean),
         };
      });

   return [...interactionItems, ...eventItems].sort(
      (a, b) => Date.parse(b.ts) - Date.parse(a.ts)
   );
}
